import { ReactElement, useMemo } from "react";
import { useQuery, useQueryClient } from "react-query";
import { ReactQueryKeys } from "../../../Constants/ClientRoutingConstants";
import ErrorCard from "../../Shared/Components/Cards/ErrorCard";
import LoadingSpinner from "../../Shared/Components/LoadingSpinner";
import RequirementsTable from "../Components/RequirementsTable";
import requirementApiService from "../Utilities/RequirementApiService";
import { RequirementReadDto } from "../../../Models/Requirement/RequirementReadDto";
import { RequirementReadTableDto } from "../../../Models/Requirement/RequirementReadTableDto";
import { RequirementCreateDto } from "../../../Models/Requirement/RequirementCreateDto";
import { rruleFrequencyToText, rruleRecurrenceToText, rruleSummaryToText } from "../Utilities/RequirementUtilities";
import { ConsumableReadDto } from "../../../Models/Consumable/ConsumableReadDto";
import consumablesApiService from "../../Consumables/Utilities/ConsumablesApiService";
import { useWindowSize } from "usehooks-ts";
import { Dialog } from "@progress/kendo-react-dialogs";
import { useLocalization } from "@progress/kendo-react-intl";
import { enMessages } from "../../../messages/en-US";
import moment from "moment";

interface Props {
  isOpenDialog: any;
  setIsOpenDialog: any;
}

export default function RequirementPage(props: Props): ReactElement {
  //Server side state
  const client = useQueryClient();
  const localizationService = useLocalization();

  //get window size
  const { height } = useWindowSize()

  const requirementsQuery = useQuery<Array<RequirementReadDto>>(ReactQueryKeys.AllRequirementQuery, async () => {
    return requirementApiService.getAllRequirements();
  });

    const consumablesQuery = useQuery<Array<ConsumableReadDto>>(ReactQueryKeys.AllConsumablesQuery, async () => {
    return consumablesApiService.getAllConsumables();
  });

  const createRequirement = async (createRequirement: RequirementCreateDto) => {
    await requirementApiService.createRequirement(createRequirement);
    client.refetchQueries(ReactQueryKeys.AllRequirementQuery);
  };


  const updateRequirements = async (patternId: string, updatePattern: RequirementCreateDto) => {
    await requirementApiService.updateRequirement(updatePattern, patternId);
    //If update is successful, refetch the updated list of areas and show success message
    client.refetchQueries(ReactQueryKeys.AllRequirementQuery);
  };

  /**
   * This function prepares data for schedules table, including injecting additional metadata.
   */
  const requirementData = useMemo(() => {
    const consumableIdToName: { [id: string]: string } = {};
    consumablesQuery.data?.forEach((consumable) => {
      consumableIdToName[consumable.id] = consumable.name;
    });
    
    return requirementsQuery.data?.map((pattern) => {
      // Add safety check for nested properties
      const rRule = pattern?.requirementDetails?.rightTimeDetails?.rRule;
      const volumeRequirement = pattern?.requirementDetails?.rightAmountDetails?.volumeRequirement;
      const consumableId = pattern?.requirementDetails?.rightGreaseDetails?.consumableId;
      const startDate = pattern?.requirementDetails?.rightTimeDetails?.startDate;

      return {
        ...pattern,
        frequency: rruleFrequencyToText(rRule),
        recurrence: rruleRecurrenceToText(rRule),
        summary: `${consumableIdToName[consumableId] || ''}, ${volumeRequirement || 0} cc, ${rruleSummaryToText(rRule) || ''}`,
        consumableName: consumableIdToName[consumableId] || '',
        startDate: startDate ? moment(startDate).format("DD/MM/YYYY") : '',
      } as RequirementReadTableDto;
    });
  }, [requirementsQuery.data, consumablesQuery.data]);

  //Render the Schedules Table
  const RenderQuery = () => {
    if (requirementsQuery.error || consumablesQuery.error) {
      return <ErrorCard />;
    } else {
      return (
        <div>
          <RequirementsTable
            requirements={requirementData!}
            consumables={consumablesQuery.data!}
            createRequirement={createRequirement}
            updateRequirements={updateRequirements}
            height={690}
          />
          {props.isOpenDialog.requirements && (
            <Dialog
              title={localizationService.toLanguageString('custom.requirements', enMessages.custom.requirements)}
              onClose={() => props.setIsOpenDialog({...props.isOpenDialog, requirements: false})}
            >
              <RequirementsTable
                requirements={requirementData!}
                consumables={consumablesQuery.data!}
                createRequirement={createRequirement}
                updateRequirements={updateRequirements}
                height={height - 200}
              />
            </Dialog>
          )}
        </div>
      );
    }
  };

  return requirementsQuery.isLoading || consumablesQuery.isLoading || requirementsQuery.isFetching || consumablesQuery.isFetching ? <LoadingSpinner /> : <RenderQuery />;
}
